import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { 
  Package, Edit, Trash2, Search, AlertTriangle, X 
} from 'lucide-react';
import LoadingSpinner from '../../ui/LoadingSpinner';
import {
  getInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getInventoryStats
} from '../../../redux/api';
import { su } from '../../inventory/stockUi';
import NumberField, { parseNumber } from '../../inventory/NumberField';
import DeviceCategorySelect from '../../inventory/DeviceCategorySelect';
import SupplierSelect from '../../inventory/SupplierSelect';
import AssetCategoryFields, { resolveAssetCategory, showAssetCategoryConflict } from '../../inventory/AssetCategoryFields';

const formatMoney = (value) =>
  new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(Number(value) || 0);

const warehouseStockValue = (item) =>
  (Number(item.currentQuantity) || 0) * (Number(item.sellingPrice) || 0);

const isLowStock = (item) =>
  Number(item.minQuantity) > 0 && Number(item.currentQuantity) <= Number(item.minQuantity);

const InventoryView = ({ stockKind = 'WAREHOUSE' }) => {
  const { t } = useTranslation();
  const isAsset = stockKind === 'ASSET';
  const emptyForm = {
    itemCode: '',
    name: '',
    description: '',
    category: '',
    newCategory: '',
    deviceCategoryId: '',
    unit: 'kom',
    purchasePrice: '',
    sellingPrice: '',
    vatRate: isAsset ? '' : 20,
    minQuantity: '',
    currentQuantity: '',
    supplierId: '',
    supplierName: '',
    notes: '',
    warehouseLocation: '',
    storageSector: '',
    storageBin: '',
    valuationMethod: 'AVERAGE',
    stockKind
  };
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [lowStockFilter, setLowStockFilter] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [stockKind]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await getInventoryItems({ stockKind });
      setItems(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju artikala');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getInventoryStats({ stockKind });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let category = formData.category;
    if (isAsset) {
      try {
        category = await resolveAssetCategory({
          selectedName: formData.category,
          newName: formData.newCategory,
        });
      } catch (categoryError) {
        if (categoryError.code === 'BOTH') {
          showAssetCategoryConflict();
          return;
        }
        toast.error(categoryError.response?.data?.message || 'Greška pri čuvanju kategorije');
        return;
      }
    }
    const payload = {
      ...formData,
      category,
      newCategory: undefined,
      purchasePrice: parseNumber(formData.purchasePrice),
      sellingPrice: parseNumber(formData.sellingPrice),
      vatRate: parseNumber(formData.vatRate, isAsset ? 0 : 20),
      minQuantity: parseNumber(formData.minQuantity),
      currentQuantity: parseNumber(formData.currentQuantity),
      stockKind,
      ...(isAsset ? { deviceCategoryId: null } : {})
    };
    try {
      if (editingItem) {
        await updateInventoryItem({ id: editingItem._id, itemData: payload });
        toast.success(isAsset ? 'Stavka inventara je ažurirana' : 'Artikal je ažuriran');
      } else {
        await createInventoryItem(payload);
        toast.success(isAsset ? 'Stavka inventara je dodata' : 'Artikal je kreiran');
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ ...emptyForm });
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri čuvanju artikla');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      itemCode: item.itemCode,
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      newCategory: '',
      deviceCategoryId: item.deviceCategory?._id || item.deviceCategory || '',
      unit: item.unit,
      purchasePrice: item.purchasePrice || '',
      sellingPrice: item.sellingPrice || '',
      vatRate: item.vatRate || (isAsset ? '' : 20),
      minQuantity: item.minQuantity || '',
      currentQuantity: item.currentQuantity || '',
      supplierId: item.supplierId || '',
      supplierName: item.supplierName || item.arrivalSource || '',
      notes: item.notes || '',
      warehouseLocation: item.warehouseLocation || '',
      storageSector: item.storageSector || '',
      storageBin: item.storageBin || '',
      valuationMethod: item.valuationMethod || 'AVERAGE',
      stockKind: item.stockKind || stockKind
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj artikal?')) {
      return;
    }
    try {
      await deleteInventoryItem(id);
      toast.success('Artikal je obrisan');
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri brisanju artikla');
    }
  };

  const categories = [...new Set(items.map(item => item.category).filter(Boolean))];
  
  const filteredItems = items.filter(item => {
    const matchesSearch = !searchTerm ||
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.arrivalSource?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    
    const matchesLowStock = !lowStockFilter || 
      (item.currentQuantity <= item.minQuantity && item.minQuantity > 0);
    
    return matchesSearch && matchesCategory && matchesLowStock;
  });

  const lowStockItems = items.filter(item => 
    item.currentQuantity <= item.minQuantity && item.minQuantity > 0
  );

  if (loading) {
    return (
      <div className={`${su.page} flex items-center justify-center`}>
        <LoadingSpinner size="lg" color="white" />
      </div>
    );
  }

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <div className={su.toolbar}>
            <div className="min-w-0">
              <h1 className={su.title}>
                <Package className={su.icon} />
                {isAsset
                  ? t('inventory.assetsTitle', 'Inventar')
                  : t('inventory.warehouseTitle', 'Magacin')}
              </h1>
              <p className={su.subtitle}>
                {isAsset
                  ? t('inventory.assetsSubtitle', 'Kancelarijski pribor, nameštaj i ostala sredstva firme')
                  : t('inventory.warehouseSubtitle', 'Roba za intervencije, reverse i servis')}
              </p>
            </div>
            <div className={su.searchWrap}>
              <Search className={su.searchIcon} />
              <input
                type="text"
                placeholder={t('inventory.search', 'Pretraži...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={su.searchInput}
              />
            </div>
          </div>

          {stats && (
            <div className={`grid grid-cols-2 ${isAsset ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-2 mt-2.5`}>
              <div className={su.stat}>
                <div className={su.statLabel}>{t('inventory.totalItems', 'Artikala')}</div>
                <div className={su.statValue}>{stats.totalItems || 0}</div>
              </div>
              <div className={su.stat}>
                <div className={su.statLabel}>{t('inventory.totalValue', 'Vrednost')}</div>
                <div className={su.statValue}>
                  {formatMoney(stats.totalValue || 0)}
                </div>
              </div>
              <div className={su.stat}>
                <div className={su.statLabel}>{t('inventory.totalQuantity', 'Količina')}</div>
                <div className={su.statValue}>{stats.totalQuantity || 0}</div>
              </div>
              {!isAsset && (
                <div className={su.stat}>
                  <div className={su.statLabel}>{t('inventory.lowStock', 'Niska zaliha')}</div>
                  <div className="text-sm font-semibold text-red-400 mt-0.5">{stats.lowStockItems || 0}</div>
                </div>
              )}
            </div>
          )}

          {!isAsset && lowStockItems.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-400 bg-red-900/20 border border-red-700/60 rounded-md px-2.5 py-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {t('inventory.lowStockAlert', 'Upozorenje:')} {lowStockItems.length} {t('inventory.itemsLowStock', 'artikala sa niskom zalihom')}
            </div>
          )}
        </div>

        {/* Form */}
        {showForm && editingItem && (
          <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 p-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-white">
                {editingItem ? t('inventory.editItem', 'Izmeni artikal') : t('inventory.addItem', 'Dodaj artikal')}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingItem(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-2">
              {isAsset ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                        {t('inventory.itemCode', 'Šifra artikla')} *
                      </label>
                      <input
                        type="text"
                        name="itemCode"
                        value={formData.itemCode}
                        onChange={handleInputChange}
                        required
                        className={su.input}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                        {t('inventory.name', 'Ime artikla')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={su.input}
                      />
                    </div>
                    <AssetCategoryFields
                      selectedName={formData.category}
                      newName={formData.newCategory}
                      onChange={({ selectedName, newName }) => setFormData((prev) => ({
                        ...prev,
                        category: selectedName,
                        newCategory: newName,
                      }))}
                    />
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                        {t('inventory.quantity', 'Količina')} *
                      </label>
                      <NumberField
                        name="currentQuantity"
                        value={formData.currentQuantity}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                        {t('inventory.inputs.unitPrice', 'Jedinična cena')}
                      </label>
                      <NumberField
                        name="purchasePrice"
                        value={formData.purchasePrice}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                        {t('inventory.vatRate', 'PDV stopa (%)')}
                      </label>
                      <NumberField
                        name="vatRate"
                        value={formData.vatRate}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        placeholder="Ako ima"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                        {t('inventory.inputs.partnerName', 'Dobavljač')}
                      </label>
                      <SupplierSelect
                        valueId={formData.supplierId}
                        valueName={formData.supplierName}
                        onChange={({ id, name }) => setFormData((prev) => ({
                          ...prev,
                          supplierId: id,
                          supplierName: name
                        }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                      {t('common.notes', 'Napomene')}
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="2"
                      className={su.textarea}
                    />
                  </div>
                </>
              ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.itemCode', 'Šifra artikla')} *
                  </label>
                  <input
                    type="text"
                    name="itemCode"
                    value={formData.itemCode}
                    onChange={handleInputChange}
                    required
                    className={su.input}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.name', 'Naziv')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={su.input}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.category', 'Kategorija')}
                  </label>
                  <DeviceCategorySelect
                    valueId={formData.deviceCategoryId}
                    onChange={({ id, name }) => setFormData((prev) => ({
                      ...prev,
                      deviceCategoryId: id,
                      category: name
                    }))}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.unit', 'Jedinica mere')} *
                  </label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    required
                    className={su.input}
                  >
                    <option value="kom">kom</option>
                    <option value="kg">kg</option>
                    <option value="m">m</option>
                    <option value="l">l</option>
                    <option value="m²">m²</option>
                    <option value="m³">m³</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.quantity', 'Količina')} *
                  </label>
                  <NumberField
                    name="currentQuantity"
                    value={formData.currentQuantity}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.purchasePrice', 'Nabavna cena')} *
                  </label>
                  <NumberField
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.sellingPrice', 'Prodajna cena')}
                  </label>
                  <NumberField
                    name="sellingPrice"
                    value={formData.sellingPrice}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.minQuantity', 'Min. količina za obaveštenje')}
                  </label>
                  <NumberField
                    name="minQuantity"
                    value={formData.minQuantity}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                    {t('inventory.inputs.partnerName', 'Dobavljač')}
                  </label>
                  <SupplierSelect
                    valueId={formData.supplierId}
                    valueName={formData.supplierName}
                    onChange={({ id, name }) => setFormData((prev) => ({
                      ...prev,
                      supplierId: id,
                      supplierName: name
                    }))}
                  />
                </div>
              </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                  }}
                  className="inline-flex items-center h-8 px-3 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-md transition"
                >
                  {t('common.cancel', 'Otkaži')}
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center h-8 px-3 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-md transition"
                >
                  {editingItem ? t('common.save', 'Sačuvaj') : t('common.create', 'Kreiraj')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Items List */}
        <div className="bg-gray-800/90 rounded-lg border border-gray-700/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={su.tableHead}>
                <tr>
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('inventory.itemCode', 'Šifra')}
                  </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.name', 'Naziv')}
                      </th>
                      {isAsset ? (
                    <>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.category', 'Kategorija')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.quantity', 'Količina')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.inputs.unitPrice', 'Jedinična cena')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.vatRate', 'PDV')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.inputs.partnerName', 'Dobavljač')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('common.notes', 'Napomene')}
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.category', 'Kategorija')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.inputs.partnerName', 'Dobavljač')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.quantity', 'Količina')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.reserved', 'Rezervisano')}
                      </th>
                      <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.available', 'Dostupno')}
                      </th>
                      <th className="px-2.5 py-1.5 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.purchasePrice', 'Nabavna/kom')}
                      </th>
                      <th className="px-2.5 py-1.5 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.sellingPrice', 'Prodajna/kom')}
                      </th>
                      <th className="px-2.5 py-1.5 text-right text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                        {t('inventory.totalValue', 'Ukupna vrednost')}
                      </th>
                    </>
                  )}
                  <th className="px-2.5 py-1.5 text-left text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                    {t('common.actions', 'Akcije')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={isAsset ? '9' : '11'} className="px-3 py-6 text-center text-xs text-gray-500">
                      {t('inventory.noItems', 'Nema artikala')}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-700">
                      <td className="px-2.5 py-1.5 text-xs text-white">{item.itemCode}</td>
                      <td className="px-2.5 py-1.5 text-xs text-white">{item.name}</td>
                      {isAsset ? (
                        <>
                          <td className="px-2.5 py-1.5 text-xs text-gray-400">{item.category || '-'}</td>
                          <td className="px-2.5 py-1.5 text-xs text-white">{item.currentQuantity || 0}</td>
                          <td className="px-2.5 py-1.5 text-xs text-white">
                            {new Intl.NumberFormat('sr-RS', { style: 'currency', currency: 'RSD' }).format(item.purchasePrice || 0)}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-gray-300">
                            {item.vatRate ? `${item.vatRate}%` : '-'}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-gray-400">{item.supplierName || '-'}</td>
                          <td className="px-2.5 py-1.5 text-xs text-gray-400 max-w-xs truncate">{item.notes || '-'}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-2.5 py-1.5 text-xs text-gray-400">
                            {item.deviceCategory?.name || item.category || '-'}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-gray-400">{item.supplierName || item.arrivalSource || '-'}</td>
                          <td className="px-2.5 py-1.5 text-xs text-white">
                            {item.currentQuantity || 0} {item.unit}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-yellow-400">
                            {item.reservedQuantity || 0} {item.unit}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-green-400">
                            <span className="inline-flex items-center gap-1">
                              {item.availableQuantity || 0} {item.unit}
                              {isLowStock(item) && (
                                <AlertTriangle className="inline-block h-4 w-4 text-yellow-500 shrink-0" title="Količina ispod minimuma" />
                              )}
                            </span>
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">
                            {formatMoney(item.purchasePrice)}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">
                            {formatMoney(item.sellingPrice)}
                          </td>
                          <td className="px-2.5 py-1.5 text-xs text-white text-right font-medium">
                            {formatMoney(warehouseStockValue(item))}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Item Details Modal */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-white">{selectedItem.name}</h2>
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.itemCode', 'Šifra')}</div>
                    <div className="text-white font-medium">{selectedItem.itemCode}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.category', 'Kategorija')}</div>
                    <div className="text-white font-medium">{selectedItem.category || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.currentQuantity', 'Trenutna količina')}</div>
                    <div className="text-white font-medium">
                      {selectedItem.currentQuantity || 0} {selectedItem.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.reserved', 'Rezervisano')}</div>
                    <div className="text-yellow-400 font-medium">
                      {selectedItem.reservedQuantity || 0} {selectedItem.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.available', 'Dostupno')}</div>
                    <div className="text-green-400 font-medium">
                      {selectedItem.availableQuantity || 0} {selectedItem.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.minQuantity', 'Minimalna zaliha')}</div>
                    <div className="text-white font-medium">
                      {selectedItem.minQuantity || 0} {selectedItem.unit}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.purchasePrice', 'Nabavna cena')}</div>
                    <div className="text-white font-medium">
                      {formatMoney(selectedItem.purchasePrice || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.sellingPrice', 'Prodajna cena')}</div>
                    <div className="text-white font-medium">
                      {formatMoney(selectedItem.sellingPrice || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.totalValue', 'Ukupna vrednost')}</div>
                    <div className="text-white font-bold text-lg">
                      {formatMoney(isAsset ? (selectedItem.currentQuantity || 0) * (selectedItem.purchasePrice || 0) : warehouseStockValue(selectedItem))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t('inventory.warehouseLocation', 'Lokacija')}</div>
                    <div className="text-white font-medium">
                      {selectedItem.warehouseLocation || '-'}
                      {selectedItem.storageSector && ` / ${selectedItem.storageSector}`}
                      {selectedItem.storageBin && ` / ${selectedItem.storageBin}`}
                    </div>
                  </div>
                </div>
                {selectedItem.description && (
                  <div className="mt-4">
                    <div className="text-sm text-gray-400 mb-1">{t('inventory.description', 'Opis')}</div>
                    <div className="text-white">{selectedItem.description}</div>
                  </div>
                )}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedItem(null);
                      handleEdit(selectedItem);
                    }}
                    className="inline-flex items-center h-8 px-3 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-md transition"
                  >
                    {t('common.edit', 'Uredi')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryView;

